<template>
  <div>
    <p>
      {{ introText }}
      {{ $t('Please note that your update will not automatically appear in the Alternative Fueling Station Locator. All station information must be confirmed before changes are made. The turnaround time for updates will depend on the completeness of the information provided as well as the responsiveness of the station or point of contact. We will contact you directly if we have any questions about your submission.') }}
      <span v-html="$sprintf($t('If you have more than one location to add or update, contact the <a href=\'%s\'>Technical Response Service</a> for a spreadsheet.'), 'mailto:technicalresponse@icf.com?subject=Station Update Spreadsheet')"></span>
    </p>

    <div v-if="$store.getters.getLoadingStationAddEdit" class="afdc-large-spinner-container">
      <div class="afdc-large-spinner"></div>
    </div>

    <template v-if="formSubmitted && !$store.getters.getStationAddEditError">
      <div v-if="formValidated" class="afdc-alert afdc-alert-success">
        {{ $t('Thank you for your station submission. Please note that your update will not automatically appear in the Alternative Fueling Station Locator. All station information must be confirmed before changes are made. The turnaround time for updates will depend on the completeness of the information provided as well as the responsiveness of the station or point of contact.')}}
        <span v-if="comments === 'specific'">{{ $t('We will contact you directly if we have any questions about your submission.') }} </span>
      </div>

      <div v-else class="afdc-alert afdc-alert-danger">
        {{ $t('This form cannot be submitted. See the errors marked below.') }}
      </div>
    </template>

    <div v-if="$store.getters.getStationAddEditError" class="afdc-alert afdc-alert-warning">
      <em>{{ $store.getters.getStationAddEditError }}</em>
    </div>

    <station-network-alert
      v-bind:fuels="formData.fuels"
      v-bind:ev-network="formData.ev_network"
    />

    <div class="afdc-col-sm-12 afdc-specific-radio" v-if="(!formValidated || $store.getters.getStationAddEditError) && this.$route.name ==='StationEdit'">
      <p>{{ $t('What feedback would you like to provide?') }}</p>
      <form>
        <div class="afdc-custom-control afdc-custom-radio afdc-custom-control-inline">
          <input type="radio" id="afdc-edit-specific" value="specific" v-model="comments" name="comment-question" class="afdc-custom-control-input">
          <label class="afdc-custom-control-label" for="afdc-edit-specific">{{ $t('Specific station details') }}</label>
        </div>
        <div class="afdc-custom-control afdc-custom-radio afdc-custom-control-inline">
          <input type="radio" id="afdc-edit-general" value="general" v-model="comments" name="comment-question" class="afdc-custom-control-input">
          <label class="afdc-custom-control-label" for="afdc-edit-general">{{ $t('General comments') }}</label>
        </div>
      </form>
    </div>

    <form v-if="!formValidated || $store.getters.getStationAddEditError" @submit.prevent="validateBeforeSubmit" id="afdc-station-form">
      <input type="hidden" v-model="formData.id" name="id">

      <div v-if="comments === 'specific'">
        <p><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> <small>{{ $t('Required field') }}</small></p>

        <h3>{{ $t('Your Contact Information') }}</h3>
        <div class="afdc-row">
          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-first-name"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('First Name') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-first-name" v-model="formData.first_name" v-validate="checkRequired" name="first_name">
                <span v-show="errors.has('first_name')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
              </div>
            </div>
          </div>

          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-last-name"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Last Name') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-last-name" v-model="formData.last_name" v-validate="checkRequired" name="last_name">
                <span v-show="errors.has('last_name')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="afdc-row">
          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-email"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Email') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-email" v-model="formData.email" v-validate="'required|email'" name="email" data-vv-validate-on="blur">
                <span v-show="errors.has('email')" class="afdc-input-error">{{ $t('A valid email address is required.') }}</span>
              </div>
            </div>
          </div>

          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-phone"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Phone') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-phone" v-model="formData.phone" v-validate="checkRequired" name="phone">
                <span v-show="errors.has('phone')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
              </div>
            </div>
          </div>
        </div>

        <template v-if="$store.getters.localeCountry !== 'CA'">
          <div class="afdc-form-group afdc-row">
            <label class="afdc-col-md-6 afdc-col-form-label" for="afdc-user-coalition"> {{ $t('If you are part of Clean Cities, indicate which coalition.') }}</label>
            <div class="afdc-col-md-6">
              <select class="afdc-custom-select" id="afdc-user-coalition" v-model="formData.coalition" name="coalition">
                <option value=""></option>
                <option v-for="option in coalitions" v-bind:key="option.id" v-bind:value="option.name">
                  {{ option.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="afdc-alert afdc-alert-warning" v-if="formData.coalition" v-html="$sprintf($t('If you have more than one station to add or update, use the Station Update Spreadsheet in the <a href=\'%s\'>Coalition Toolbox</a>.'), 'https://cleancities.energy.gov/toolbox/alternative-fuel-station-report/')"></div>
        </template>

        <h3>{{ $t('Station Information') }}</h3>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-name"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr>{{ $t('Station Name') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-name" v-model="formData.station_name" v-validate="'required'" name="station_name">
            <span v-show="errors.has('station_name')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Available Fuel(s)') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(fuel, code) in $store.getters.loaderFuelNamesByKey">
              <div v-if="code !== 'all'" v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.fuels" v-bind:id="`afdc-station-fuels-${code}`" class="afdc-custom-control-input" v-validate="'required'" name="fuels">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-fuels-${code}`">{{ fuel }}</label>
              </div>
            </template>
            <span v-show="errors.has('fuels')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <!-- This needs to be directly under the list of Fuels so that the user can be told ASAP that we cannot control charging network data. -->
        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-ev-network">
            <abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr>
            {{ $t('Electric Charging Network') }}
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-ev-network" v-model="formData.ev_network" name="ev_network" v-validate="requireIfFuelIsChecked(formData.fuels, 'ELEC')">
              <option value=""></option>
              <option v-for="networkOption in stationEvNetworks(networkOptions)" :value="networkOption.key" :key="networkOption.key">{{ networkOption.name }} </option>
            </select>
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('ev_network')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <station-network-alert
          v-bind:fuels="formData.fuels"
          v-bind:ev-network="formData.ev_network"
        />

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-country"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Country') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-country" v-model="formData.country" v-validate="'required'" name="country">
              <option value=""></option>
              <option v-for="option in countryOptions" v-bind:key="option.key" v-bind:value="option.key">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('country')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-address"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Station Address') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-address" v-model="formData.street_address" v-validate="'required'" name="street_address">
            <span v-show="errors.has('street_address')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-city"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('City') }}</label>
          <div class="afdc-col-md-5">
            <input type="text" class="afdc-form-control" id="afdc-station-city" v-model="formData.city" v-validate="'required'" name="city">
            <span v-show="errors.has('city')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-state"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ formData.country === 'CA' ? $t('Province/Territory') : $t('State') }}</label>
          <div class="afdc-col-md-2">
            <input type="text" class="afdc-form-control" id="afdc-station-state" v-model="formData.state" v-validate="'required'" name="state">
          </div>
          <div class="afdc-col-md-6">
            <span v-show="errors.has('state')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-zip"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Postal Code') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-zip" v-model="formData.zip" v-validate="'required'" name="zip">
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('zip')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-access">
            <abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr>
            {{ $t('Type of Access') }}
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-access" v-model="formData.access" v-validate="'required'" name="access" ref="access">
              <option value=""></option>
              <option value="Public">{{ $t('Public') }}</option>
              <option value="Private">{{ $t('Private') }}</option>
            </select>
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('access')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.access && formData.access.includes('Public')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-access-restrictions">
            {{ $t('Access Restrictions for Customers') }}
          </label>
          <div class="afdc-col-md-8">
            <input
              id="afdc-access-restrictions"
              class="afdc-form-control"
              type="text"
              v-model="formData.access_restrictions"
              name="access_restrictions"
            />
            <em>{{ $t('Note any restrictions. For example: Access is restricted to customers of a business.') }}</em>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-web-form-status">
            <abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr>
            {{ $t('Status') }}
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-web-form-status" v-model="formData.web_form_status" v-validate="'required'" name="web_form_status">
              <option value=""></option>
              <option value="existing">{{ $t('Existing') }}</option>
              <option value="planned">{{ $t('Planned') }}</option>
              <option value="temporarily_unavailable">{{ $t('Temporarily Unavailable') }}</option>
              <option value="out_of_service">{{ $t('Out of Service') }}</option>
            </select>
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('web_form_status')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.web_form_status === 'existing' || formData.web_form_status === 'temporarily_unavailable'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="datepicker-element-open">{{ $t('Open Date') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" id="open-date-picker" class="afdc-form-control" v-model="formData.open_date" key="open-date"/>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.web_form_status && (formData.web_form_status === 'planned' || formData.web_form_status === 'temporarily_unavailable')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="datepicker-element-open">{{ $t('Expected Open/Reopen Date') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" id="expected-date-picker" class="afdc-form-control" v-model="formData.expected_date" key="expected-date" />
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.web_form_status && formData.web_form_status === 'out_of_service'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-service-description">{{ $t('Indicate when the station went out of service and why it closed') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-service-description" v-model="formData.service_description" name="service_description">
            <span class="afdc-form-hint">{{ $t('In addition to marking the station as out of service on this form, please report any broken equipment or access issues to the station owner.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Payment Types') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(payment, code) in $store.getters.loaderPaymentMethodNamesByKey">
              <div v-if="code !== 'all'" v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.payments" v-bind:id="`afdc-station-payments-${code}`" class="afdc-custom-control-input" name="payments">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-payments-${code}`">{{ payment }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-phone">{{ $t('Station Phone') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-phone" v-model="formData.station_phone" name="station_phone">
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-hours">
            <abbr v-show="formData.access && formData.access.includes('Public')" v-bind:title="$t('Required')" class="afdc-required">*</abbr>
            {{ $t('Business Hours') }}
          </label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-hours" v-model="formData.access_days_time" name="access_days_time" v-validate="'required_if:access,Public'">
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('access_days_time')" class="afdc-input-error">
              {{ $t('This field is required.') }}
            </span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-contact">{{ $t('Station Point of Contact') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-contact" v-model="formData.point_of_contact" name="point_of_contact">
            <em>{{ $t('If you are not the owner and/or operator of this station, provide contact information (name, phone number, email address, and/or website).') }}</em>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-latitude">{{ $t('Latitude') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-latitude" v-model="formData.latitude" name="latitude">
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-longitude">{{ $t('Longitude') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-longitude" v-model="formData.longitude" name="longitude">
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-allowed-vehicles">
            {{ $t('Type of Vehicles Fueled (for fleet fueling facilities)') }}
          </label>
          <div class="afdc-col-md-4">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input v-model="formData.allowed_vehicles" type="checkbox" id="allowed-vehicles-passenger" name="allowed_vehicles" value="passenger" class="afdc-custom-control-input">
              <label class="afdc-custom-control-label" for="allowed-vehicles-passenger">{{ $t('Passenger vehicles (class 1-2)') }}</label>
            </div>
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input v-model="formData.allowed_vehicles" type="checkbox" id="allowed-vehicles-medium" name="allowed_vehicles" value="medium" class="afdc-custom-control-input">
              <label class="afdc-custom-control-label" for="allowed-vehicles-medium">{{ $t('Medium-duty (class 3-5)') }}</label>
            </div>
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input v-model="formData.allowed_vehicles" type="checkbox" id="allowed-vehicles-heavy" name="allowed_vehicles" value="heavy" class="afdc-custom-control-input">
              <label class="afdc-custom-control-label" for="allowed-vehicles-heavy">{{ $t('Heavy-duty (class 6-8)') }}</label>
            </div>
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input v-model="formData.allowed_vehicles" type="checkbox" id="allowed-vehicles-other" name="allowed_vehicles" value="other" class="afdc-custom-control-input">
              <label class="afdc-custom-control-label" for="allowed-vehicles-other">{{ $t('Other') }}</label>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.allowed_vehicles.includes('other')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-allowed-vehicles-description">
            {{ $t('Describe Type of Vehicles Allowed') }}
          </label>
          <div class="afdc-col-md-8">
            <input
              id="afdc-allowed-vehicles-description"
              class="afdc-form-control"
              type="text"
              v-model="formData.allowed_vehicles_description"
              name="allowed_vehicles_description"
            />
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-maximum-vehicle-class">
            {{ $t('Largest Vehicle Type (ability to access the station)') }}
          </label>
          <div class="afdc-col-md-4">
            <select class="afdc-custom-select" id="afdc-maximum-vehicle-class" v-model="formData.maximum_vehicle_class" name="maximum_vehicle_class">
              <option value=""></option>
              <option value="passenger">{{ $t('Passenger vehicles (class 1-2)') }}</option>
              <option value="medium">{{ $t('Medium-duty (class 3-5)') }}</option>
              <option value="heavy">{{ $t('Heavy-duty (class 6-8)') }}</option>
              <option value="other">{{ $t('Other') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.maximum_vehicle_class === 'other'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-maximum-vehicle-class-description">
            {{ $t('Describe Largest Vehicle Type') }}
          </label>
          <div class="afdc-col-md-8">
            <input
              id="afdc-maximum-vehicle-class-description"
              class="afdc-form-control"
              type="text"
              v-model="formData.maximum_vehicle_class_description"
              name="maximum_vehicle_type_description"
            />
          </div>
        </div>

        <h3 v-show="formData.fuels.length > 0">{{ $t('Fuel Information') }}</h3>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('BD')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-bd-blends">{{ $t('Biodiesel Blends Available') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-bd-blends" v-model="formData.bd_blends" name="bd_blends">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-pressure">{{ $t('CNG Fill Pressure (psi)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-pressure" v-model="formData.ng_psi" name="ng_psi">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-type">{{ $t('CNG Fill Type') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-cng-type" v-model="formData.ng_fill_type_code" name="ng_fill_type_code">
              <option value=""></option>
              <option value="Q">{{ $t('Fast-fill') }}</option>
              <option value="T">{{ $t('Time-fill') }}</option>
              <option value="B">{{ $t('Both: fast-fill and time-fill') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-dispenser">
            <abbr v-show="formData.access && formData.access.includes('Public')" v-bind:title="$t('Required')" class="afdc-required">*</abbr>
            {{ $t('CNG Dispensers Installed') }}
          </label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-dispenser" v-model="formData.cng_dispenser_num" name="cng_dispenser_num" v-validate="'required_if:access,Public'">
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('cng_dispenser_num')" class="afdc-input-error">
              {{ $t('This field is required.') }}
            </span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-compression">{{ $t('CNG Capacity per Compressor (standard cubic feet per minute)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-compression" v-model="formData.cng_total_compression" name="cng_compression">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-total-storage">{{ $t('CNG Total Storage Capacity (standard cubic feet)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-total-storage" v-model="formData.cng_total_storage" name="cng_storage">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-has-rng">{{ $t('CNG Sold as Renewable Natural Gas (RNG)') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-cng-has-rng" v-model="formData.cng_has_rng" name="cng_has_rng">
              <option value=""></option>
              <option value="true">{{ $t('Yes') }}</option>
              <option value="false">{{ $t('No') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('CNG') && formData.cng_has_rng === 'true'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-renewable-source">
            {{ $t('CNG Renewable Energy Used (if RNG is produced on site)') }}
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-cng-renewable-source" v-model="formData.cng_renewable_source" name="cng_renewable_source">
              <option value=""></option>
              <template v-for="(source, code) in $store.getters.loaderRenewableSourceNamesByKey">
                <option v-bind:value="code" v-bind:key="code">{{ $t(source) }}</option>
              </template>
            </select>
          </div>
        </div>
        <div v-if="formData.fuels && formData.fuels.includes('ELEC')">
          <!-- We add the disable modifier to v-validate to prevent this from validating on every 'input' Event -->
          <!-- See https://vee-validate.logaretm.com/v2/guide/events.html#disabling-events-validation for details. -->
          <input
            v-show="false"
            v-model="formData.ev_levels"
            v-validate.disable="requireAtLeastOneEVFuelType"
            name="ev_level_checkboxes"
          />
          <div v-if="errors.has('ev_level_checkboxes')">
            <div class="afdc-alert afdc-alert-danger">
              <div v-html="$t('At least one type of electric vehicle charging is required.')"></div>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <div class="afdc-col-md-7">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input
                type="checkbox"
                v-on:input="$validator.validate('ev_level_checkboxes')"
                v-model="formData.ev_levels"
                value="level1"
                id="afdc-station-level-1"
                class="afdc-custom-control-input"
                name="ev_levels"
              >
              <label class="afdc-custom-control-label" for="afdc-station-level-1">{{ $t('Level 1 electric vehicle charging available') }}</label>
            </div>
          </div>

          <div class="afdc-col-md-5" v-if="formData.fuels && formData.fuels.includes('ELEC')">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-8 afdc-col-form-label" for="afdc-station-level-1-count">{{ $t('Number of Level 1 Ports') }}</label>
              <div class="afdc-col-md-4">
                <input type="text" class="afdc-form-control" id="afdc-station-level-1-count" v-model="formData.ev_level1_evse_num" name="ev_level1_evse_num" v-validate="requireIfLevelIsChecked(formData.ev_levels, 'level1')">
              </div>
              <span v-show="errors.has('ev_level1_evse_num')" class="afdc-input-error afdc-col-md-12 afdc-col-form-label">{{ $t('This field is required.') }}</span>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <div class="afdc-col-md-7">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input
                type="checkbox"
                v-on:input="$validator.validate('ev_level_checkboxes')"
                v-model="formData.ev_levels"
                value="level2"
                id="afdc-station-level-2"
                class="afdc-custom-control-input"
                name="ev_levels"
              >
              <label class="afdc-custom-control-label" for="afdc-station-level-2">{{ $t('Level 2 electric vehicle charging available') }}</label>
            </div>
          </div>

          <div class="afdc-col-md-5" v-if="formData.fuels && formData.fuels.includes('ELEC')">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-8 afdc-col-form-label" for="afdc-station-level-2-count">{{ $t('Number of Level 2 Ports') }}</label>
              <div class="afdc-col-md-4">
                <input type="text" class="afdc-form-control" id="afdc-station-level-2-count" v-model="formData.ev_level2_evse_num" name="ev_level2_evse_num" v-validate="requireIfLevelIsChecked(formData.ev_levels, 'level2')">
              </div>
              <span v-show="errors.has('ev_level2_evse_num')" class="afdc-input-error afdc-col-md-12 afdc-col-form-label">{{ $t('This field is required.') }}</span>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <div class="afdc-col-md-7">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input
                type="checkbox"
                v-on:input="$validator.validate('ev_level_checkboxes')"
                v-model="formData.ev_levels"
                value="dc_fast"
                id="afdc-station-dc-fast"
                class="afdc-custom-control-input"
                name="ev_levels"
              >
              <label class="afdc-custom-control-label" for="afdc-station-dc-fast">{{ $t('DC fast charging available') }}</label>
            </div>
          </div>

          <div class="afdc-col-md-5">
            <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('ELEC')">
              <label class="afdc-col-md-8 afdc-col-form-label" for="afdc-station-dc-fast-count">{{ $t('Number of DC Fast Ports') }}</label>
              <div class="afdc-col-md-4">
                <input type="text" class="afdc-form-control" id="afdc-station-dc-fast-count" v-model="formData.ev_dc_fast_num" name="ev_dc_fast_num" v-validate="requireIfLevelIsChecked(formData.ev_levels, 'dc_fast')">
              </div>
              <span v-show="errors.has('ev_dc_fast_num')" class="afdc-input-error afdc-col-md-12 afdc-col-form-label">{{ $t('This field is required.') }}</span>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('ELEC')">
          <!-- We add the disable modifier to v-validate to prevent this from validating on every 'input' Event -->
          <!-- See https://vee-validate.logaretm.com/v2/guide/events.html#disabling-events-validation for details. -->
          <input
            v-show="false"
            v-model="formData.ev_connector_types"
            v-validate.disable="requireAtLeastOneEVConnectorType"
            name="ev_connector_type_checkboxes"
          />
          <div v-if="errors.has('ev_connector_type_checkboxes')" class="afdc-col-md-12">
            <div class="afdc-alert afdc-alert-danger">
              <div v-html="$t('At least one electric charging connector is required.')"></div>
            </div>
          </div>

          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Electric Charging Connectors') }}</label>
          <div class="afdc-col-md-8">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station"
            v-for="(name, code) in connectorOptions" v-bind:key="`charger-${code}`">
              <input
                type="checkbox"
                v-on:input="$validator.validate('ev_connector_type_checkboxes')"
                v-bind:value="code"
                v-model="formData.ev_connector_types"
                v-bind:id="`afdc-charger-${code}`"
                class="afdc-custom-control-input"
                name="ev_connector_types"
              >
              <label class="afdc-custom-control-label" v-bind:for="`afdc-charger-${code}`">{{ name }}</label>
            </div>
          </div>
        </div>

        <div v-if="formData.fuels && formData.fuels.includes('ELEC')">
          <template v-for="connector in Object.keys(formData.ev_power_outputs)">
            <div
              :key="`${connector}-power-output-field`"
              class="afdc-form-group afdc-row"
              v-if="formData.ev_connector_types.includes(connector)"
            >
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-power-output">
                {{ connectorOptions[connector] }} {{ $t('Power Output (kW)') }}
              </label>
              <div class="afdc-col-md-3">
                <input
                  type="text"
                  class="afdc-form-control"
                  id="afdc-station-power-output"
                  v-model="formData.ev_power_outputs[connector]"
                  :name="`${connector}_power_output`"
                />
              </div>
            </div>
          </template>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-electric-pricing">{{ $t('Electric Charging Pricing') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-electric-pricing" v-model="formData.ev_pricing" name="ev_pricing">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-ev-renewable-source">{{ $t('Electric Charging Renewable Energy Used') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-ev-renewable-source" v-model="formData.ev_renewable_source" name="ev_renewable_source">
              <option value=""></option>
              <template v-for="(source, code) in $store.getters.loaderRenewableSourceNamesByKey">
                <option v-bind:value="code" v-bind:key="code">{{ $t(source) }}</option>
              </template>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-elec-manufacturer">{{ $t('Electric Charging Manufacturer') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-elec-manufacturer" v-model="formData.post_manufacturer" name="post_manufacturer">
              <option value=""></option>
              <template v-for="(source, code) in $store.getters.postManufacturerNamesByKey">
                <option v-bind:value="code" v-bind:key="code">{{ source }}</option>
              </template>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('E85')">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Ethanol Other Blends Offered') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(blend, code) in $store.getters.loaderEthanolBlendNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.e85_other_ethanol_blends" v-bind:id="`afdc-station-ethanol-blend-${code}`" class="afdc-custom-control-input" name="ethanol_blends">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-ethanol-blend-${code}`">{{ blend }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('HY')">
          <label class="afdc-col-md-4 afdc-col-form-label"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Hydrogen Pressure') }}</label>
          <div class="afdc-col-md-8">
            <input v-show="false" v-model="formData.hy_pressures" v-validate=requireAtLeastOneCheckbox(this.formData.hy_pressures) name="hydrogen_pressure_checkboxes" value/>
            <div v-if="errors.has('hydrogen_pressure_checkboxes')">
              <div class="afdc-alert afdc-alert-danger" v-html="$t('At least one hydrogen pressure is required.')"></div>
            </div>
            <template v-for="(pressure, code) in $store.getters.loaderHydrogenPressureNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.hy_pressures" v-bind:id="`afdc-station-hy-pressure-${code}`" class="afdc-custom-control-input" name="hy_pressure">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-hy-pressure-${code}`">{{ pressure }} {{ $t('bar') }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('HY')">
          <label class="afdc-col-md-4 afdc-col-form-label"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Hydrogen SAE Standards Compliance') }}</label>
          <div class="afdc-col-md-8">
            <input v-show="false" v-model="formData.hy_standards" v-validate=requireAtLeastOneCheckbox(this.formData.hy_standards) name="hydrogen_standards_checkboxes" value/>
            <div v-if="errors.has('hydrogen_standards_checkboxes')">
              <div class="afdc-alert afdc-alert-danger" v-html="$t('At least one hydrogen SAE standard is required.')"></div>
            </div>
            <template v-for="(standard, code) in $store.getters.loaderHydrogenStandardNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.hy_standards" v-bind:id="`afdc-station-hy-standard-${code}`" class="afdc-custom-control-input" name="hy_standards">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-hy-standard-${code}`">{{ $t('SAE') }} {{ standard }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('LPG')">
          <label class="afdc-col-md-4 afdc-col-form-label"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Propane Nozzles') }}</label>
          <div class="afdc-col-md-8">
            <input v-show="false" v-model="formData.lpg_nozzle_types" v-validate="requireAtLeastOneLPGNozzleType" name="lpg_nozzle_checkboxes" value/>
            <div v-if="errors.has('lpg_nozzle_checkboxes')">
              <div class="afdc-alert afdc-alert-danger" v-html="$t('At least one type of propane nozzle is required.')"></div>
            </div>
            <template v-for="(nozzle, code) in $store.getters.loaderLpgNozzleTypeNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.lpg_nozzle_types" v-bind:id="`afdc-station-lpg-nozzle-${code}`" class="afdc-custom-control-input" name="lpg_nozzle">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-lpg-nozzle-${code}`">{{ nozzle }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('LPG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-sufficient-fill-rate">
            {{ $t('Propane Sufficient Fill Rate') }}
            <span
              class="afdc-stations-tooltip"
              v-tippy="{arrow : true, arrowType : 'round', content: propaneFillRateTooltipText}"
            >
              <font-awesome-icon icon="question-circle"/>
            </span>
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-sufficient-fill-rate" v-model="formData.has_sufficient_fill_rate" name="sufficient_fill_rate">
              <option value=""></option>
              <option value="true">{{ $t('Yes') }}</option>
              <option value="false">{{ $t('No') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-if="formData.fuels && formData.fuels.includes('LPG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-renewable-propane">
            {{ $t('Renewable Propane Sold or Dispensed') }}
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-renewable-propane" v-model="formData.lpg_renewable_propane" name="renewable_propane">
              <option value=""></option>
              <option value="true">{{ $t('Yes') }}</option>
              <option value="false">{{ $t('No') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('LNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-lng-has-rng">{{ $t('LNG Sold as Renewable Natural Gas (RNG)') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-lng-has-rng" v-model="formData.lng_has_rng" name="lng_has_rng">
              <option value=""></option>
              <option value="true">{{ $t('Yes') }}</option>
              <option value="false">{{ $t('No') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('LNG') && formData.lng_has_rng==='true'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-lng-renewable-source">
            {{ $t('LNG Renewable Energy Used (if RNG is produced on site)') }}
          </label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-lng-renewable-source" v-model="formData.lng_renewable_source" name="lng_renewable_source">
              <option value=""></option>
              <template v-for="(source, code) in $store.getters.loaderRenewableSourceNamesByKey">
                <option v-bind:value="code" v-bind:key="code">{{ $t(source) }}</option>
              </template>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('RD')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-rd-blends">{{ $t('Renewable Diesel Blends Available') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-rd-blends" v-model="formData.rd_blends" name="rd_blends">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('RD')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-rd-blended">{{ $t('Renewable Diesel Blended with Biodiesel') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-rd-blended" v-model="formData.rd_blended" name="rd_blended">
              <option value=""></option>
              <option value="yes">{{ $t('Yes') }}</option>
              <option value="no">{{ $t('No') }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="afdc-form-group">
        <h3 v-if="comments === 'specific'"><label class="afdc-col-form-label" for="afdc-station-comments">{{ $t('Additional Information') }}</label></h3>
        <h3 v-else><label class="afdc-col-form-label" for="afdc-station-comments"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr>{{ $t('Comments') }}</label></h3>
        <p>{{ $t('Include any relevant information such as a website where additional station information can be found or notes about the station.') }}</p>
        <textarea type="text" class="afdc-form-control" id="afdc-station-comments" v-model="formData.comments" v-validate="requiredGeneral" name="comments"></textarea>
        <span v-show="errors.has('comments')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
      </div>

      <button type="submit" class="afdc-btn afdc-btn-success afdc-float-right">{{ $t('Submit') }}</button>
    </form>
  </div>
</template>

<script>
import Vue from 'vue';
import StationNetworkAlert from '@/components/StationNetworkAlert.vue';
import handleErrors from '@/lib/handleErrors';
import reject from 'lodash/reject';
import Flatpickr from 'flatpickr';

export default {
  components: {
    StationNetworkAlert,
  },
  props: ['update-type'],
  data() {
    // If certain fields depend on the value of other fields, make
    // sure that the source field is listed here in the return value.
    // They don't have to be present for the page to render, but if
    // a field omitted here *is* assigned in copyStationData(),
    // then Vue will NOT track its changes in the StationEdit form.
    return {
      formData: {
        fuels: [],
        country: null,
        access: '',
        access_restrictions: '',
        web_form_status: '',
        open_date: '',
        expected_date: '',
        payments: [],
        allowed_vehicles: [],
        maximum_vehicle_class: '',
        cng_has_rng: false,
        ev_levels: [],
        ev_network: '',
        ev_power_outputs: {
          J1772: null,
          J1772COMBO: null,
          CHADEMO: null,
          TESLA: null,
        },
        ev_connector_types: [],
        lng_has_rng: false,
        hy_pressures: [],
        hy_standards: [],
        e85_other_ethanol_blends: [],
        lpg_nozzle_types: [],
        coalition: '',
      },
      formSubmitted: false,
      formValidated: false,
      formError: false,
      comments: 'specific',
      coalitions: null,
    };
  },
  computed: {
    introText() {
      if (this.$props.updateType === 'editing') {
        return this.$t('Use this form to suggest updates for the station.');
      }

      return this.$t('Use this form to suggest adding a station.');
    },

    propaneFillRateTooltipText() {
      const tooltipTag = document.createElement('p');
      tooltipTag.style = 'font-family: Karla, sans-serif';
      tooltipTag.innerHTML = this.$t('Can the station fill a vehicle in about the same amount of time it takes to fuel a gasoline vehicle?');
      return tooltipTag;
    },

    countryOptions() {
      return reject(this.$store.getters.loaderCountries, { key: 'all' });
    },

    networkOptions() {
      return reject(this.$store.getters.loaderElecNetworks, { key: 'all' });
    },

    connectorOptions() {
      const connectors = { ...this.$store.getters.loaderElecConnectorsByKey };
      const connectorsByLevel = {};

      if (this.formData.ev_levels.includes('level1')) {
        connectorsByLevel.NEMA1450 = connectors.NEMA1450;
        connectorsByLevel.NEMA515 = connectors.NEMA515;
        connectorsByLevel.NEMA520 = connectors.NEMA520;
      }

      if (this.formData.ev_levels.includes('level2')) {
        connectorsByLevel.J1772 = connectors.J1772;
      }

      if (this.formData.ev_levels.includes('level2') || this.formData.ev_levels.includes('dc_fast')) {
        connectorsByLevel.TESLA = connectors.TESLA;
      }

      if (this.formData.ev_levels.includes('dc_fast')) {
        connectorsByLevel.J1772COMBO = connectors.J1772COMBO;
        connectorsByLevel.CHADEMO = connectors.CHADEMO;
      }

      return connectorsByLevel;
    },

    checkRequired() {
      return this.comments === 'specific' ? 'required' : '';
    },
    requiredGeneral() {
      return this.comments === 'general' ? 'required' : '';
    },

    // We want at least one EV fuel checkbox selected (only checked if Electric has been selected as a fuel)
    requireAtLeastOneEVFuelType() {
      const providedAtLeastOneFuelType = this.formData.ev_levels.length;
      return !providedAtLeastOneFuelType ? 'required' : '';
    },

    // We want at least one EV connector checkbox selected (only checked if Electric has been selected as a fuel)
    requireAtLeastOneEVConnectorType() {
      const providedAtLeastOneConnectorType = this.formData.ev_connector_types.length;
      return !providedAtLeastOneConnectorType ? 'required' : '';
    },

    requireAtLeastOneLPGNozzleType() {
      const providedAtLeastOneNozzleType = this.formData.lpg_nozzle_types.length;
      return !providedAtLeastOneNozzleType ? 'required' : '';
    },
  },
  mounted() {
    Flatpickr('#open-date-picker', {
      static: true,
      dateFormat: 'Y-m-d',
    });

    Flatpickr('#expected-date-picker', {
      dateFormat: 'Y-m-d',
      static: true,
    });
    Vue.axios
      .get('https://cleancities.energy.gov/api/v1/coalitions')
      .then((response) => {
        this.coalitions = response.data.result.sort((a, b) => ((a.name > b.name) ? 1 : -1));
      });
  },
  watch: {
    '$store.getters.stationDetails': {
      immediate: true,
      handler: 'copyStationData',
    },
    comments(newVal) {
      if (this.$route.name === 'StationEdit' && newVal === 'general') {
        this.formData.email = 'webmaster.afdc@nrel.gov';
        this.formData.comment_note = 'Only general comments were submitted. No changes were made to the edit form.';
      } else {
        this.formData.email = '';
        this.formData.comment_note = '';
      }
    },
  },
  methods: {
    copyStationData() {
      if (this.$route.name === 'StationEdit') {
        const station = this.$store.getters.stationDetails;
        this.formData.id = station.id;
        this.formData.fuels = [station.fuel_type_code];
        this.formData.country = station.country;
        this.formData.street_address = station.street_address;
        this.formData.city = station.city;
        this.formData.state = station.state;
        this.formData.zip = station.zip;
        this.formData.access = this.accessValue(station.groups_with_access_code);
        this.formData.access_restrictions = station.access_restrictions ?? '';
        this.formData.station_name = station.station_name;
        this.formData.web_form_status = this.statusValue(station.status_code);
        this.formData.open_date = station.open_date;
        this.formData.expected_date = station.expected_date;
        this.formData.payments = station.cards_accepted ? station.cards_accepted.split(' ') : [];
        this.formData.station_phone = station.station_phone;
        this.formData.access_days_time = this.$store.getters.localeLang === 'fr' ? station.access_days_time_fr : station.access_days_time;
        this.formData.latitude = station.latitude;
        this.formData.longitude = station.longitude;
        this.formData.maximum_vehicle_class = this.maximumVehicleClassValue(station.maximum_vehicle_class);
        this.formData.bd_blends = this.$store.getters.localeLang === 'fr' ? station.bd_blends_fr : station.bd_blends;
        this.formData.rd_blends = this.$store.getters.localeLang === 'fr' ? station.rd_blends_fr : station.rd_blends;
        this.formData.rd_blended = station.rd_blended;
        this.formData.ng_psi = station.ng_psi;
        this.formData.cng_total_compression = station.cng_total_compression;
        this.formData.cng_total_storage = station.cng_total_storage;
        this.formData.cng_dispenser_num = station.cng_dispenser_num;
        this.formData.ng_fill_type_code = station.ng_fill_type_code;
        this.formData.cng_renewable_source = station.cng_renewable_source;
        this.formData.cng_has_rng = station.cng_has_rng;
        this.formData.ev_network = station.ev_network;
        this.formData.ev_network_web = station.ev_network_web;
        this.formData.ev_level1_evse_num = station.ev_level1_evse_num;
        this.formData.ev_level2_evse_num = station.ev_level2_evse_num;
        this.formData.ev_dc_fast_num = station.ev_dc_fast_num;
        this.formData.ev_pricing = station.ev_pricing;
        this.formData.ev_power_outputs = station.ev_power_outputs ?? {
          J1772: null, J1772COMBO: null, CHADEMO: null, TESLA: null,
        };
        this.formData.ev_renewable_source = station.ev_renewable_source;
        this.formData.ev_connector_types = station.ev_connector_types ?? [];
        this.formData.post_manufacturer = station.post_manufacturer;
        this.formData.lng_renewable_source = station.lng_renewable_source;
        this.formData.lng_has_rng = station.lng_has_rng;
        this.formData.hy_pressures = station.hy_pressures ? station.hy_pressures : [];
        this.formData.hy_standards = station.hy_standards ? station.hy_standards : [];
        this.formData.e85_other_ethanol_blends = station.e85_other_ethanol_blends ? station.e85_other_ethanol_blends : [];
        this.formData.lpg_nozzle_types = station.lpg_nozzle_types ? station.lpg_nozzle_types : [];
        this.formData.has_sufficient_fill_rate = station.has_sufficient_fill_rate;
        this.formData.lpg_renewable_propane = station.lpg_renewable_propane;

        if (station.ev_level1_evse_num && station.ev_level1_evse_num > 0) {
          this.formData.ev_levels.push('level1');
        }
        if (station.ev_level2_evse_num && station.ev_level2_evse_num > 0) {
          this.formData.ev_levels.push('level2');
        }
        if (station.ev_dc_fast_num && station.ev_dc_fast_num > 0) {
          this.formData.ev_levels.push('dc_fast');
        }

        // Clone the initial form data onto the original_data attribute, which
        // the API will use for generating diffs of what changed.
        // Note: these JSON methods are used to prevent Vue from attaching observers
        // to fields since they are meant to be static in this case. However, the
        // tradeoff is that this approach is not sufficient if form data grows to
        // include more structured data types, like Maps, Sets, or Dates.
        this.formData.original_data = JSON.parse(JSON.stringify(this.formData));
      }
    },

    accessValue(code) {
      if (code && code.includes('Public')) {
        return 'Public';
      }
      if (code && code.includes('Private')) {
        return 'Private';
      }
      return null;
    },

    statusValue(code) {
      if (code && code === 'E') {
        return 'existing';
      }
      if (code && code === 'P') {
        return 'planned';
      }
      if (code && code === 'T') {
        return 'temporarily_unavailable';
      }
      return null;
    },

    maximumVehicleClassValue(code) {
      switch (code) {
        case 'LD':
          return 'passenger';
        case 'MD':
          return 'medium';
        case 'HD':
          return 'heavy';
        default:
          return 'other';
      }
    },

    // Make sure a number is supplied if the corresponding EV fuel checkbox is checked
    requireIfLevelIsChecked(levels, level) {
      const isLevelChecked = levels.includes(level);
      return isLevelChecked ? 'required' : '';
    },

    // Make sure a specific fuel is selected before making a field required
    requireIfFuelIsChecked(fuels, fuel) {
      const isFuelChecked = fuels.includes(fuel);
      return isFuelChecked ? 'required' : '';
    },

    // Checkbox selections end up in arrays in our form data. Should the field array be empty, this means there are no checkboxes ticked.
    // We enforce that at least one should be ticked by setting an invisible input field in the DOM to be required next to the actual field.
    // Since it is invisible, an error will occur upon submit, which we can use to inform the user to check one of the options.
    requireAtLeastOneCheckbox(field) {
      return field.length ? '' : 'required';
    },

    validateBeforeSubmit() {
      document.getElementById('afdc-alt-fuel-station').scrollTop = 0;
      this.$store.commit('setLoadingStationAddEdit', true);
      this.$validator.validateAll().then((result) => {
        if (result) {
          Vue.axios.request({
            url: `${process.env.VUE_APP_API_URL_ROOT}/v1/locator/${this.$route.name === 'StationEdit' ? 'update_station_mail' : 'create_station_mail'}.json`,
            method: this.$route.name === 'StationEdit' ? 'put' : 'post',
            params: {
              api_key: window.afdcStationsOptions.apiKey,
            },
            data: JSON.stringify({
              ...this.formData,
              locale: this.$store.getters.locale,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          })
            .then(() => {
              this.$store.commit('setLoadingStationAddEdit', false);
              this.$store.commit('setStationAddEditError', null);
              this.formSubmitted = true;
              this.formValidated = true;
            })
            .catch((e) => {
              this.$store.commit('setLoadingStationAddEdit', false);
              this.$store.commit('setStationAddEditError', handleErrors(e));
              this.formSubmitted = false;
              this.formValidated = true;
            });
        } else {
          this.$store.commit('setLoadingStationAddEdit', false);
          this.$store.commit('setStationAddEditError', null);
          this.formSubmitted = true;
          this.formValidated = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/_mixins.scss';

@include media-breakpoint-up(md) {
  .col-form-label {
    text-align: right;
  }
}

.custom-select {
  option {
    color:#495057;
  }
  optgroup {
    color:#495057;
  }
}
.input-error {
  color: #a94442;
}

abbr.required {
  font-weight: bold;
  color: #e87723;
  cursor: inherit;
  border-style: none;
  text-decoration: none;
}

.specific-radio {
  background-color: #f6f6f6;
  padding: 15px;
  margin-bottom: 15px;
}

.custom-checkbox-station {
  background-color: #f6f6f6;
  display: block;
  border-bottom: 1px solid #ddd;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover {
    background-color: #eee;
  }

  .custom-control-label {
    display: block;
    padding: 10px 25px;
    &:hover {
      cursor: pointer;
    }
  }

  .custom-control-label::before {
    width: 28px;
    height: 28px;
    margin-left: 10px;
    top: 9px;
    border-radius: 50%;
    background-color: #ddd;
    &:hover {
      cursor: pointer;
    }
  }

  .custom-control-label::after {
    width: 28px;
    height: 28px;
    margin-left: 10px;
    top: 9px;
  }

  .custom-control-input {
    margin-right: 20px;
  }

  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #66bb6a;
  }
}

  .flatpickr-input {
    background-color: #fff !important;
  }

 .form-hint {
    font-size: 14px;
    font-style: italic;
  }
</style>
